import { useRef, useState, useEffect } from 'react';
import { motion, useInView } from 'framer-motion';
import { ArrowUpRight } from 'lucide-react';

const stats = [
  { raw: 1000, suffix: '+', label: 'Consulting Engagements', display: '1,000+' },
  { raw: null,  suffix: '',  label: 'Data Science · Distinction Expected', display: 'MSc' },
  { raw: 12,    suffix: '+', label: 'Projects Delivered', display: '12+' },
  { raw: null,  suffix: '',  label: 'BSc Economics', display: '2:1' },
];

const workFocus = [
  'Predictive Analytics & ML Modelling',
  'Business Intelligence & Data Visualisation',
  'Strategy & Consulting Analysis',
  'LLM Evaluation & AI Quality',
  'Econometrics & Statistical Research',
];

const containerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.08 } },
};

const itemVariants = {
  hidden: { opacity: 0, x: -14 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.45, ease: [0.25, 0.1, 0.25, 1] } },
};

/* ─── Animated count-up stat ────────────────────────────────────────── */
const CountUpStat = ({ stat, delay = 0 }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-60px' });
  const [displayed, setDisplayed] = useState('0');

  useEffect(() => {
    if (!inView) return;
    if (stat.raw === null) { setDisplayed(stat.display); return; }

    const target = stat.raw;
    const duration = 1600;
    const startTime = performance.now() + delay * 1000;
    let raf;

    const tick = (now) => {
      if (now < startTime) { raf = requestAnimationFrame(tick); return; }
      const progress = Math.min((now - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3); // ease-out cubic
      const current = Math.round(eased * target);
      setDisplayed(
        current >= 1000
          ? `${Math.floor(current / 1000).toLocaleString()},${String(current % 1000).padStart(3, '0')}${stat.suffix}`
          : `${current}${stat.suffix}`
      );
      if (progress < 1) raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, stat, delay]);

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, scale: 0.94 }}
      whileInView={{ opacity: 1, scale: 1 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay }}
      className="p-6 rounded-2xl border bg-bg-surface/60"
      style={{ borderColor: 'rgba(255,255,255,0.07)' }}
    >
      <p className="font-display text-3xl font-medium mb-1.5 gradient-text-brand stat-number">
        {displayed}
      </p>
      <p className="text-[11px] text-text-secondary tracking-wide leading-snug">
        {stat.label}
      </p>
    </motion.div>
  );
};

/* ─── Section ─────────────────────────────────────────────────────────── */
const About = () => {
  const scrollToContact = () => {
    document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="about" className="relative py-20 sm:py-28 md:py-40" data-testid="about-section">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-[1.15fr_1fr] gap-16 lg:gap-24 xl:gap-32 items-start">

          {/* ── Left ────────────────────────────────────────── */}
          <motion.div
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.8, ease: [0.25, 0.1, 0.25, 1] }}
          >
            <div className="section-label mb-8">About</div>

            <h2 className="font-display text-4xl sm:text-5xl lg:text-[3.2rem] xl:text-[3.5rem] leading-[1.06] tracking-tight font-medium mb-10">
              The numbers tell you what happened.{' '}
              <span className="gradient-text-brand">The analysis tells you what to do.</span>
            </h2>

            <div className="space-y-5 text-text-secondary text-[1.05rem] leading-[1.75] max-w-[530px]">
              <p>
                BSc Economics from Westminster. MSc Data Science at Loughborough.
                Most technical people can build models but miss the commercial logic.
                Most business graduates see the logic but can't quantify it.{' '}
                <span className="text-white font-medium">
                  Having both means I can work through either end of the same problem.
                </span>
              </p>
              <p>
                Over 1,000 consulting engagements across four expert networks, advising investors and
                strategy teams on AI and data. Twelve projects across ML, econometrics,
                business intelligence, and strategy.{' '}
                <span className="text-white font-medium">
                  I know how AI systems fail in practice, and I've seen analysis through from raw data
                  to a decision someone actually made.
                </span>
              </p>
            </div>

            <motion.button
              onClick={scrollToContact}
              className="group flex items-center gap-2 mt-10 px-6 py-3.5 border border-white/[0.16] rounded-full text-sm font-medium hover:border-white/38 hover:bg-white/[0.04] transition-all duration-300"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.97 }}
            >
              Get in touch
              <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </motion.button>
          </motion.div>

          {/* ── Right ───────────────────────────────────────── */}
          <motion.div
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.8, delay: 0.12, ease: [0.25, 0.1, 0.25, 1] }}
          >
            {/* Animated count-up stats */}
            <div className="grid grid-cols-2 gap-3.5 mb-12">
              {stats.map((stat, i) => (
                <CountUpStat key={i} stat={stat} delay={i * 0.06} />
              ))}
            </div>

            {/* Work focus list */}
            <div>
              <p className="text-[10px] uppercase tracking-[0.32em] text-text-secondary font-medium mb-5">
                What I work on
              </p>
              <motion.div
                variants={containerVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: '-40px' }}
                className="space-y-0"
              >
                {workFocus.map((item, i) => (
                  <motion.div
                    key={i}
                    variants={itemVariants}
                    className="flex items-center gap-4 py-3.5 border-b last:border-0"
                    style={{ borderColor: 'rgba(255,255,255,0.05)' }}
                  >
                    <div
                      className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                      style={{ backgroundColor: 'rgba(112, 0, 255, 0.65)' }}
                    />
                    <span className="text-sm text-white/75 font-medium">{item}</span>
                  </motion.div>
                ))}
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default About;
